import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedCart = localStorage.getItem('cart');
      if (storedCart) {
        setCart(JSON.parse(storedCart));
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cart', JSON.stringify(cart));
    }
  }, [cart]);

  const addToCart = (item) => {
    console.log('Adding item to cart:', item);
    setCart(prev => {
      const programId = item.programId || item._id || item.id;
      const formatId = item.formatId;
      const date = item.date;
      const title = item.title;

      if (!programId) {
        console.error('Cannot add item to cart - missing programId');
        return prev;
      }

      // Generate a more robust unique ID using program, format, title and date
      // This ensures that even if formatId is generic (e.g. "odc"), different titles will stay separate
      const titleSlug = title ? title.toLowerCase().replace(/[^a-z0-9]/g, '-') : 'no-title';
      const cartItemId = `${programId}-${formatId}-${titleSlug}${date ? `-${new Date(date).getTime()}` : ''}`;

      const newItem = {
        ...item,
        programId,
        id: programId, // Backward compatibility
        cartItemId
      };

      // Check if this specific combination is already in cart
      // We check for cartItemId OR if it's a legacy item without one, match on programId+formatId
      if (!prev.find(i => (i.cartItemId === cartItemId) || (!i.cartItemId && i.programId === programId && i.formatId === formatId))) {
        console.log('Adding new item to cart:', newItem);
        const newCart = [...prev, newItem];
        console.log('New cart state:', newCart);
        return newCart;
      }
      console.log('Item already in cart:', cartItemId);
      return prev;
    });
  };

  const removeFromCart = (cartItemId, legacyId) => {
    setCart(prev => prev.filter(item => {
      if (cartItemId && item.cartItemId) {
        return item.cartItemId !== cartItemId;
      }
      // Fallback for legacy items without cartItemId
      const id = item.id || item._id || item.programId;
      return id !== legacyId && item.cartItemId !== cartItemId;
    }));
  };

  const clearCart = () => {
    setCart([]);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('cart');
    }
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, clearCart, showCart, setShowCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
